import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Container, SectionLabel, CTASection } from "@/components/primitives";
import { SITE } from "@/data/site";

const CHAPTERS = [
  {
    n: "01",
    title: "Experience You Can Build On",
    body: "I have spent more than fifteen years building software across engineering, AI, quality and technical leadership. That range means I can move between writing code, shaping architecture and making the decisions that determine whether a product actually succeeds. For a client, it means fewer surprises and software that holds up as the business grows.",
  },
  {
    n: "02",
    title: "What Adobe Taught Me",
    body: "At Adobe I helped build enterprise products relied on by millions of people. Working at that scale sets a high bar. Software has to be correct, performant and maintainable for years, not just until launch day. Those standards now shape everything I build, whether it is for a large organization or an early-stage startup.",
  },
  {
    n: "03",
    title: "Building With Business In Mind",
    body: "Good engineering starts with a clear understanding of the problem and the people it affects. I care as much about why software is built as how it is built, because that is what separates features people use from features they ignore. Every technical decision I make is tied to a business reason.",
  },
  {
    n: "04",
    title: "How I Approach Software",
    body: "Today I design products, build modern web applications and integrate AI into real workflows. I favour software that is simple to understand, easy to maintain and genuinely useful. The goal is never the most technology. It is the right technology, applied where it earns its place.",
  },
  {
    n: "05",
    title: "Engineering And Business Together",
    body: "Through TrueQual Dataworks I deliver consulting and software development for startups and established businesses. Running my own company alongside my engineering work has taught me to balance engineering excellence with commercial reality: to know where quality is worth the investment, and where shipping sooner serves the business better.",
  },
];

export default function About() {
  return (
    <>
      <SEO
        title="About"
        description="Vivek Chandra has spent more than fifteen years building software across engineering, AI, quality and technical leadership, including over a decade at Adobe. He founded TrueQual Dataworks to help businesses build software they can depend on."
        path="/about"
        image={SITE.portrait}
      />

      <section className="relative pt-36 md:pt-44">
        <Container>
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <SectionLabel>About</SectionLabel>
                <h1 className="mt-8 font-cabinet text-4xl font-medium leading-[1.05] tracking-tight text-primary-ink sm:text-5xl lg:text-6xl">
                  Building software that solves meaningful problems.
                </h1>
              </motion.div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                className="relative"
              >
                <div className="pointer-events-none absolute -inset-6 -z-10 rounded-full bg-accent/15 blur-[90px]" />
                <div className="relative overflow-hidden border border-white/10">
                  <img
                    src={SITE.portrait}
                    alt="Portrait of Vivek Chandra"
                    className="aspect-[4/5] w-full object-cover object-top grayscale contrast-[1.05]"
                    data-testid="about-portrait"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-base/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-white/10 bg-black/40 px-5 py-3 backdrop-blur-md">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary-ink">
                      {SITE.name}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink">
                      Est. 1989
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      <div className="mt-24 md:mt-32">
        <Marquee />
      </div>

      <section className="py-24 md:py-32" data-testid="about-chapters">
        <Container>
          <div className="mx-auto max-w-4xl">
            {CHAPTERS.map((c) => (
              <Reveal
                key={c.n}
                className="grid gap-6 border-t border-white/10 py-12 md:grid-cols-12 md:gap-12 md:py-16"
              >
                <div className="md:col-span-3">
                  <span className="font-mono text-4xl font-medium text-accent md:text-5xl">
                    {c.n}
                  </span>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-cabinet text-2xl font-medium tracking-tight text-primary-ink md:text-3xl">
                    {c.title}
                  </h2>
                  <p className="mt-4 text-lg leading-relaxed text-secondary-ink">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}

            <Reveal className="border-t border-white/10 py-16 md:py-24">
              <p className="font-cabinet text-3xl font-medium leading-[1.2] tracking-tight text-primary-ink md:text-4xl">
                I believe good software isn't measured by the technologies it
                uses.{" "}
                <span className="text-muted-ink">
                  It's measured by the problems it solves.
                </span>
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      <CTASection />
    </>
  );
}
